import { Link } from "react-router-dom";
import DoospLayout from "@/components/doosp/DoospLayout";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
      <h2 className="font-display text-2xl font-bold tracking-tight mt-12 mb-4">{title}</h2>
      <div className="prose prose-zinc max-w-none text-zinc-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  const updated = "17 February 2026";
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">
            Legal · Privacy
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="privacy-heading">
            Privacy Policy
          </h1>
          <div className="text-xs font-mono text-zinc-500 mt-4">Last updated: {updated}</div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16 pb-24">
        <nav className="text-sm text-zinc-600 mb-10 border-l-2 border-red-800 pl-5 space-y-1.5" aria-label="Contents">
          {[
            ["what", "1. What we collect"],
            ["why", "2. Why we collect it"],
            ["share", "3. Who we share it with"],
            ["retain", "4. How long we keep it"],
            ["rights", "5. Your rights"],
            ["cookies", "6. Cookies & local storage"],
            ["security", "7. Security"],
            ["children", "8. Children"],
            ["changes", "9. Changes to this policy"],
            ["contact", "10. Contact"],
          ].map(([id, label]) => (
            <div key={id}><a href={`#${id}`} className="hover:text-red-800">{label}</a></div>
          ))}
        </nav>

        <Section id="what" title="1. What we collect">
          <p>To operate the competency assessment platform, IMCAC collects the following:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account data</strong> — full name, email, hashed password, profession (optional).</li>
            <li><strong>Assessment data</strong> — exam attempts, answers, scores, timestamps, case-study text.</li>
            <li><strong>Certificate data</strong> — issued certificate IDs, issue and expiry dates, holder name and email.</li>
            <li><strong>Payment metadata</strong> — PayPal order reference, amount, currency, payment status. We do <strong>not</strong> store card numbers or PayPal credentials; these are handled entirely by PayPal.</li>
            <li><strong>Technical data</strong> — IP address on login/registration (for fraud prevention), browser type, and session cookies.</li>
          </ul>
        </Section>

        <Section id="why" title="2. Why we collect it">
          <ul className="list-disc pl-6 space-y-1">
            <li>To authenticate you and secure your account.</li>
            <li>To administer, grade, and audit the competency assessment.</li>
            <li>To issue, deliver, and verify your certificate.</li>
            <li>To process one-time issuance fees via PayPal.</li>
            <li>To prevent fraud, cheating, and abuse.</li>
            <li>To comply with legal, tax, and accounting obligations.</li>
          </ul>
        </Section>

        <Section id="share" title="3. Who we share it with">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>PayPal</strong> — payment processing (name, email, amount).</li>
            <li><strong>Anthropic (Claude)</strong> — pseudonymous case-study text is sent to Claude Sonnet for AI grading. No personally identifying data is included in the grading prompt.</li>
            <li><strong>Resend</strong> — transactional email delivery (name, email, certificate PDF attachment) once configured.</li>
            <li><strong>Cloud infrastructure (Emergent / MongoDB Atlas)</strong> — hosting.</li>
          </ul>
          <p>
            IMCAC does <strong>not</strong> sell or rent personal data. We do not use your data for advertising.
          </p>
        </Section>

        <Section id="retain" title="4. How long we keep it">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account &amp; assessment data</strong> — retained while your account is active and for 2 years after the last certificate expires, to support verification.</li>
            <li><strong>Certificate metadata</strong> — retained indefinitely so anyone can verify a historical credential.</li>
            <li><strong>Payment records</strong> — retained for the period required by applicable tax and accounting law (typically 7 years in India).</li>
            <li><strong>Server logs &amp; IPs</strong> — 90 days, then automatically discarded.</li>
          </ul>
        </Section>

        <Section id="rights" title="5. Your rights">
          <p>Regardless of jurisdiction, you may at any time request that we:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access the personal data we hold about you.</li>
            <li>Correct inaccuracies in your data.</li>
            <li>Delete your account (this will revoke active certificates; historical verification metadata may be retained in redacted form).</li>
            <li>Export your assessment data in a portable format.</li>
          </ul>
          <p>
            Send requests to{" "}
            <a href="mailto:admin@imcac.org" className="text-red-800 underline">admin@imcac.org</a>.
            We respond within 30 days.
          </p>
        </Section>

        <Section id="cookies" title="6. Cookies & local storage">
          <p>We use a small number of strictly-necessary browser storage items:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>httpOnly session cookie</strong> — authenticates your logged-in session. Cannot be read by JavaScript, so cannot be stolen by cross-site scripting.</li>
            <li><strong>localStorage</strong> — exam-in-progress recovery state, corporate order drafts, disclaimer acknowledgement. Never contains passwords or auth tokens.</li>
          </ul>
          <p>We do not use any advertising, analytics, or tracking cookies.</p>
        </Section>

        <Section id="security" title="7. Security">
          <p>
            Passwords are hashed with bcrypt. Communication with our servers is encrypted in transit (TLS 1.2+). Certificate PDFs are generated server-side per-request and are not stored on disk. PayPal handles all card data; we never see it.
          </p>
        </Section>

        <Section id="children" title="8. Children">
          <p>
            The platform is intended for use by adults 18 years or older. We do not knowingly collect personal data from children.
          </p>
        </Section>

        <Section id="changes" title="9. Changes to this policy">
          <p>
            Material changes will be announced by email to registered users and by a banner on the site. Continued use after a change constitutes acceptance of the revised policy.
          </p>
        </Section>

        <Section id="contact" title="10. Contact">
          <p>
            Data protection questions:{" "}
            <a href="mailto:admin@imcac.org" className="text-red-800 underline">admin@imcac.org</a>.
            See also our{" "}
            <Link to="/terms" className="text-red-800 underline">Terms of Service</Link>.
          </p>
        </Section>
      </div>
    </DoospLayout>
  );
}
